import React from "react";
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Typography } from "@mui/material";
import {
    AssignmentOutlined as AssignmentOutlinedIcon,
    RecordVoiceOverOutlined as RecordVoiceOverOutlinedIcon,
} from "@mui/icons-material";
import { FormattedMessage } from "react-intl";
import { useNavigate } from "react-router-dom";
import { routes } from "../../constants";

// The two ways respondents can take an assessment (Survey.respond_mode). Both are
// the same assessment, built in the same builder; the choice only presets its
// "Respondent experience", which the builder can still change.
const OPTIONS = [
    {
        mode: "traditional",
        icon: AssignmentOutlinedIcon,
        titleId: "projects.new_assessment_survey",
        title: "Traditional",
        descriptionId: "projects.new_assessment_survey_description",
        description: "Respondents read the assessment's questions on screen and answer by typing or choosing an option.",
    },
    {
        mode: "voice",
        icon: RecordVoiceOverOutlinedIcon,
        titleId: "projects.new_assessment_voice",
        title: "Modern",
        descriptionId: "projects.new_assessment_voice_description",
        description: "The assessment's questions are read aloud, and respondents answer by speaking.",
    },
];

// "New assessment" on a project's Evidence tab: opens the survey builder with
// this project preselected, in the chosen style.
export default function NewAssessmentDialog({ open, onClose, projectId }) {
    const navigate = useNavigate();

    const pick = (mode) => {
        const query = new URLSearchParams({ project: String(projectId) });
        if (mode === "voice") query.set("mode", "voice");
        navigate(`${routes.surveys}/new?${query.toString()}`);
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle sx={{ fontWeight: 700, color: "#00438E" }}>
                <FormattedMessage id="projects.new_assessment" defaultMessage="New assessment" />
            </DialogTitle>
            <DialogContent>
                <Box sx={{ display: "flex", gap: 2, flexDirection: { xs: "column", sm: "row" }, pt: 1 }}>
                    {OPTIONS.map(({ mode, icon: Icon, titleId, title, descriptionId, description }) => (
                        <Button
                            key={mode}
                            variant="outlined"
                            onClick={() => pick(mode)}
                            sx={{
                                flex: 1,
                                p: 2.5,
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "center",
                                gap: 1,
                                textTransform: "none",
                                borderRadius: 2,
                                borderColor: "rgba(0, 67, 142, 0.3)",
                                color: "#00438E",
                                "&:hover": { borderColor: "#00438E", bgcolor: "rgba(0, 67, 142, 0.06)" },
                            }}
                        >
                            <Icon sx={{ fontSize: 36 }} />
                            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                                <FormattedMessage id={titleId} defaultMessage={title} />
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                <FormattedMessage id={descriptionId} defaultMessage={description} />
                            </Typography>
                        </Button>
                    ))}
                </Box>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
                <Button onClick={onClose} sx={{ textTransform: "none" }}>
                    <FormattedMessage id="general.cancel" defaultMessage="Cancel" />
                </Button>
            </DialogActions>
        </Dialog>
    );
}
